import { useCallback } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseHarmonicGroupMenuHandlersProps {
  selectedHarmonicLineId: string | null;
  verticalLines: VerticalLine[];
  harmonicMenuPosition: { x: number; y: number } | null;
  focusedHarmonicByChart: Record<string, string | null>;
  setSelectedHarmonicLineId: React.Dispatch<React.SetStateAction<string | null>>;
  setHarmonicMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setEditingHarmonicGroup: React.Dispatch<React.SetStateAction<any | null>>;
  setEditingCarrierGroup: React.Dispatch<React.SetStateAction<any | null>>;
  setFocusedHarmonicByChart: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  removeVerticalLine: (lineId: string) => void;
}

export interface UseHarmonicGroupMenuHandlersReturn {
  handleEdit: () => void;
  handleRemove: () => void;
  handleFocus: () => void;
}

/**
 * Handles HarmonicGroupMenu actions (Edit, Remove, Focus)
 * Manages harmonic and side bands group editing, deletion, and focus toggling
 */
export function useHarmonicGroupMenuHandlers({
  selectedHarmonicLineId,
  verticalLines,
  harmonicMenuPosition,
  focusedHarmonicByChart,
  setSelectedHarmonicLineId,
  setHarmonicMenuPosition,
  setEditingHarmonicGroup,
  setEditingCarrierGroup,
  setFocusedHarmonicByChart,
  removeVerticalLine,
}: UseHarmonicGroupMenuHandlersProps): UseHarmonicGroupMenuHandlersReturn {

  const handleEdit = useCallback(() => {
    try {
      if (!selectedHarmonicLineId) {
        return;
      }

      // Find the clicked harmonic line (can be ANY harmonic in the group)
      const clickedLine = verticalLines.find((line: VerticalLine) => line.id === selectedHarmonicLineId);

      if (!clickedLine) {
        return;
      }

      if (!clickedLine.isHarmonic) {
        return;
      }

      // Find all lines in this group (same groupId)
      const groupLines = verticalLines
        .filter((line: VerticalLine) => {
          // If we have groupId, use it for precise matching
          if (clickedLine.groupId && line.groupId) {
            return line.groupId === clickedLine.groupId;
          }
          // Fallback to old logic for backward compatibility (lines created before groupId was added)
          return line.isHarmonic && line.color === clickedLine.color && line.chartId === clickedLine.chartId;
        })
        .sort((a: VerticalLine, b: VerticalLine) => {
          const aTime = typeof a.timestamp === 'number' ? a.timestamp : a.timestamp.getTime();
          const bTime = typeof b.timestamp === 'number' ? b.timestamp : b.timestamp.getTime();
          return aTime - bTime;
        });

      // Find the actual starting point (first harmonic with isFirstHarmonic = true)
      const startingLine = groupLines.find((line: VerticalLine) => line.isFirstHarmonic) || groupLines[0];

      // Extract label prefix (e.g., "H" from "H0", "C" from "C0")
      const labelPrefix = startingLine.label.replace(/\d+$/, '').replace(/^-/, '');

      // Check if this is a carrier (C, D, E) or harmonic (H, A, B, etc.)
      const isCarrier = ['C', 'D', 'E'].includes(labelPrefix);

      // Get starting point value from the timestamp (handle both Date and number)
      const startingPoint =
        typeof startingLine.timestamp === 'number' ? startingLine.timestamp : startingLine.timestamp.getTime();

      // Determine x-axis type by checking if this is Hz-based (multiplication) or date-based (addition)
      let xAxisType: 'date' | 'numeric' | 'category' = 'numeric';
      let intervalType: 'seconds' | 'minutes' | 'hours' | 'days' = 'days';

      // CRITICAL: Check if timestamp values are small numbers (frequency data)
      // Real date timestamps are huge (e.g., 1700000000000 ms for 2023)
      // Frequency data is small (e.g., 100, 200, 300 Hz)
      if (Math.abs(startingPoint) < 1000000) {
        xAxisType = 'numeric';
      } else if (groupLines.length > 1) {
        const firstHarmonic =
          typeof groupLines[0].timestamp === 'number'
            ? groupLines[0].timestamp
            : groupLines[0].timestamp.getTime();
        const secondHarmonic =
          typeof groupLines[1].timestamp === 'number'
            ? groupLines[1].timestamp
            : groupLines[1].timestamp.getTime();

        // If second harmonic is approximately 2x the first, it's numeric (Hz)
        // Otherwise it's date-based (addition)
        const ratio = secondHarmonic / firstHarmonic;
        if (Math.abs(ratio - 2) < 0.01) {
          xAxisType = 'numeric';
        } else {
          xAxisType = 'date';
          // Try to detect interval type from the difference
          const diff = secondHarmonic - firstHarmonic;
          if (diff < 60 * 1000) intervalType = 'seconds';
          else if (diff < 60 * 60 * 1000) intervalType = 'minutes';
          else if (diff < 24 * 60 * 60 * 1000) intervalType = 'hours';
          else intervalType = 'days';
        }
      }

      if (isCarrier) {
        // For carriers: calculate interval and count
        // Carriers have: C0 (center), C1, C2, C3, -C1, -C2, -C3
        // Count = number of positive side lines (C1, C2, C3)
        // Interval = distance between C0 and C1

        // Find positive lines (excluding C0)
        const positiveLines = groupLines.filter((line: VerticalLine) => {
          const match = line.label.match(/^([A-Z])(\d+)$/);
          return match && parseInt(match[2]) > 0;
        });

        const count = positiveLines.length; // Number of side lines (e.g., 3 for C1, C2, C3)

        // Calculate sideband position from the first sideband (C1, D1, or E1)
        let sidebandPosition = startingPoint; // Default to center if no first sideband found
        if (positiveLines.length > 0) {
          // Explicitly find the first sideband (e.g., C1, D1, E1)
          const firstSideband = groupLines.find((line: VerticalLine) => {
            return line.label === `${labelPrefix}1`;
          });
          if (firstSideband) {
            sidebandPosition =
              typeof firstSideband.timestamp === 'number'
                ? firstSideband.timestamp
                : firstSideband.timestamp.getTime();
          }
        }

        const editGroup = {
          lineId: selectedHarmonicLineId,
          sectionKey: startingLine.chartId || '',
          centerPosition: startingPoint, // Center position (C0)
          sidebandPosition, // Position of first sideband (C1)
          labelPrefix,
          count, // Number of side lines
          color: startingLine.color,
          chartId: startingLine.chartId || '',
          xAxisType,
          menuPosition: harmonicMenuPosition || undefined,
          isNewGroup: false,
        };
        setEditingCarrierGroup(editGroup);
      } else {
        // For harmonics: use multiplier
        const multiplier = groupLines.length - 1;

        const editGroup = {
          lineId: selectedHarmonicLineId,
          sectionKey: startingLine.chartId || '',
          startingPoint,
          labelPrefix,
          multiplier,
          color: startingLine.color,
          chartId: startingLine.chartId || '',
          xAxisType,
          intervalType,
          position: harmonicMenuPosition || undefined, // Save the position for the popover
          isNewGroup: false, // Explicitly mark as editing existing group
        };
        setEditingHarmonicGroup(editGroup);
      }

      // Close the menu (edit popover will open with the saved position)
      setSelectedHarmonicLineId(null);
      setHarmonicMenuPosition(null);
    } catch (error) {
      // Silent error handling
    }
  }, [
    selectedHarmonicLineId,
    verticalLines,
    harmonicMenuPosition,
    setSelectedHarmonicLineId,
    setHarmonicMenuPosition,
    setEditingHarmonicGroup,
    setEditingCarrierGroup,
  ]);

  const handleRemove = useCallback(() => {
    if (selectedHarmonicLineId) {
      // Find all lines that belong to this group (same color/label prefix)
      const lineToRemove = verticalLines.find((line: VerticalLine) => line.id === selectedHarmonicLineId);
      if (lineToRemove && lineToRemove.isHarmonic) {
        const sectionKey = lineToRemove.sectionKey || lineToRemove.chartId;
        const groupColor = lineToRemove.color;
        const groupIdToRemove = lineToRemove.groupId;

        // Check if this group is currently focused
        if (sectionKey && groupIdToRemove && focusedHarmonicByChart[sectionKey] === groupIdToRemove) {
          setFocusedHarmonicByChart((prev) => ({
            ...prev,
            [sectionKey]: null,
          }));
        }

        // Remove all lines with the same groupId (proper group identification)
        const linesToRemove = verticalLines.filter((line: VerticalLine) => {
          // If we have groupId, use it for precise matching
          if (groupIdToRemove && line.groupId) {
            return line.groupId === groupIdToRemove;
          }
          // Fallback to old logic for backward compatibility (lines created before groupId was added)
          // This fallback will be removed once all existing lines are regenerated
          if (!line.isHarmonic || line.color !== groupColor || line.chartId !== lineToRemove.chartId) {
            return false;
          }
          const labelMatch = lineToRemove.label?.match(/^-?([A-Z])/);
          const labelPrefix = labelMatch ? labelMatch[1] : null;
          if (labelPrefix && line.label) {
            const lineLabelMatch = line.label.match(/^-?([A-Z])/);
            return lineLabelMatch && lineLabelMatch[1] === labelPrefix;
          }
          return true;
        });
        linesToRemove.forEach((line: VerticalLine) => removeVerticalLine(line.id));

        // Group count is automatically calculated from actual lines
      }
    }
  }, [
    selectedHarmonicLineId,
    verticalLines,
    focusedHarmonicByChart,
    setFocusedHarmonicByChart,
    removeVerticalLine,
  ]);

  const handleFocus = useCallback(() => {
    if (selectedHarmonicLineId) {
      const focusLine = verticalLines.find((line: VerticalLine) => line.id === selectedHarmonicLineId);
      if (focusLine && focusLine.isHarmonic && focusLine.sectionKey) {
        const targetSectionKey = focusLine.sectionKey;
        const currentFocus = focusedHarmonicByChart[targetSectionKey];

        // Toggle focus: if already focused, turn off, otherwise turn on
        if (currentFocus === focusLine.groupId) {
          setFocusedHarmonicByChart((prev) => ({
            ...prev,
            [targetSectionKey]: null,
          }));
        } else {
          setFocusedHarmonicByChart((prev) => {
            const newState = {
              ...prev,
              [targetSectionKey]: focusLine.groupId || null,
            };
            return newState;
          });
        }

        // Close the menu
        setSelectedHarmonicLineId(null);
        setHarmonicMenuPosition(null);
      }
    }
  }, [
    selectedHarmonicLineId,
    verticalLines,
    focusedHarmonicByChart,
    setSelectedHarmonicLineId,
    setHarmonicMenuPosition,
    setFocusedHarmonicByChart,
  ]);

  return {
    handleEdit,
    handleRemove,
    handleFocus,
  };
}
